/**
 * SubmitDemand — self-service demand submission, open to ANY logged-in user.
 *
 * Hits POST /demands/submit (useSubmitDemand). The submitter supplies CONTENT
 * ONLY (UAT decision 2026-09-11): no focus-area question, no classifier
 * feedback, nothing about internal handling on the page. The server
 * classifies the text and sets the landing itself — a wrong landing is the
 * council's to correct from the audit queue, not the submitter's to prevent.
 * Identity/stage/priority are stamped server-side; the demand lands in `new`.
 * The page still shows WHO it will be stamped as (owner ask, 2026-09-22), so
 * the submitter can confirm it before sending.
 *
 * The questions are DSAI's seven-question core (#1628, Oct 2026): four
 * required, three optional, conditional follow-ups for related work and a
 * date. Department is not asked — the server takes it from the profile.
 * Files (#1629) upload as they are picked and are bound on submit.
 */

import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  makeStyles,
  Caption1,
  tokens,
  Body1,
  Button,
  Field,
  Input,
  Radio,
  RadioGroup,
  Textarea,
  Title3,
} from '@fluentui/react-components'
import { Attach20Regular, Dismiss16Regular, DocumentAdd24Regular } from '@fluentui/react-icons'
import { useSubmitDemand } from '../hooks/useDemands'
import {
  DEMAND_ATTACHMENT_ACCEPT,
  MAX_DEMAND_ATTACHMENTS,
  uploadDemandAttachment,
} from '../hooks/useDemandAttachments'
import type { DemandAttachment, RelatedWork } from '../types/demand'
import { useEffectiveUser } from '../hooks/useEffectiveUser'
import { UserAvatar } from '../components/UserAvatar'
import { useToast } from '../components/ToastProvider'
import { apiErrorMessage } from '../lib/apiError'
import { bayerColors } from '../theme'

const useStyles = makeStyles({
  page: {
    minHeight: '100vh',
    background: tokens.colorNeutralBackground2,
    display: 'flex',
    flexDirection: 'column',
  },
  topbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 20px',
    background: tokens.colorNeutralBackground1,
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke2,
  },
  brand: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '16px',
    fontWeight: 600,
    color: bayerColors.navy,
    textDecoration: 'none',
  },
  brandLogo: { width: '26px', height: '26px', objectFit: 'contain' },
  root: {
    maxWidth: '640px',
    margin: '0 auto',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    color: bayerColors.navy,
  },
  intro: { color: tokens.colorNeutralForeground3 },
  submitter: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  submitterText: { display: 'flex', flexDirection: 'column', minWidth: 0 },
  submitterName: { fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1 },
  submitterEmail: { color: tokens.colorNeutralForeground3, overflowWrap: 'anywhere' },
  hint: { color: tokens.colorNeutralForeground3 },
  dateRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(140px, 180px) minmax(0, 1fr)',
    gap: tokens.spacingHorizontalM,
  },
  dateReason: { minWidth: 0 },
  files: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: tokens.spacingVerticalXS,
  },
  fileRow: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXS },
  fileName: { overflowWrap: 'anywhere' },
  optionalHeader: {
    marginTop: tokens.spacingVerticalM,
    fontSize: '12px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '.04em',
    color: tokens.colorNeutralForeground3,
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalM,
  },
})

interface SubmitFields {
  title: string
  description: string
  related_work: RelatedWork | ''
  related_work_details: string
  target_user: string
  anticipated_benefit: string
  has_date: 'no' | 'yes' | ''
  important_date: string
  important_date_reason: string
  background: string
}

const EMPTY: SubmitFields = {
  title: '',
  description: '',
  related_work: '',
  related_work_details: '',
  target_user: '',
  anticipated_benefit: '',
  has_date: '',
  important_date: '',
  important_date_reason: '',
  background: '',
}

/** The first required answer still missing, said as the thing to do. */
function missingAnswer(f: SubmitFields): string | null {
  if (!f.title.trim()) return 'Add a title'
  if (!f.description.trim()) return 'Describe what you need'
  if (!f.related_work) return 'Say whether it is related to existing work'
  if (!f.target_user.trim()) return 'Say who would benefit'
  return null
}

// One-time key for this filling-in of the form (#1264). It has to be minted
// when the form opens and reused across a reload — not minted on click, since
// a reload would then mint a different one, which is the same as having none.
// sessionStorage survives a reload (and is per-tab); a genuine second visit
// mints a fresh key because we clear it on a successful submit.
const IDEMPOTENCY_STORAGE_KEY = 'dac.submitDemand.idempotencyKey'

function currentIdempotencyKey(): string {
  try {
    const existing = sessionStorage.getItem(IDEMPOTENCY_STORAGE_KEY)
    if (existing) return existing
    const minted = crypto.randomUUID()
    sessionStorage.setItem(IDEMPOTENCY_STORAGE_KEY, minted)
    return minted
  } catch {
    // Private mode / storage disabled: fall back to a per-instance key. It
    // won't survive a reload, but that only forfeits dedup on that edge — a
    // resubmit is no worse than today, and the double-click guard still holds.
    return crypto.randomUUID()
  }
}

function clearIdempotencyKey(): void {
  try {
    sessionStorage.removeItem(IDEMPOTENCY_STORAGE_KEY)
  } catch {
    // Nothing to clear if storage is unavailable.
  }
}

export function SubmitDemand() {
  const styles = useStyles()
  const navigate = useNavigate()
  const toast = useToast()
  const submit = useSubmitDemand()
  // The identity the server will stamp: under View-as that is the target,
  // because the request carries the impersonation header.
  const me = useEffectiveUser()
  const [f, setF] = useState<SubmitFields>(EMPTY)
  // Stable for this form instance; survives a reload via sessionStorage. Lazy
  // initializer so the key is read/minted once, not on every render.
  const [idempotencyKey] = useState(currentIdempotencyKey)
  const [files, setFiles] = useState<DemandAttachment[]>([])
  const [uploading, setUploading] = useState(false)
  const fileInput = useRef<HTMLInputElement>(null)

  function set<K extends keyof SubmitFields>(key: K, value: SubmitFields[K]) {
    setF((prev) => ({ ...prev, [key]: value }))
  }

  const missing = missingAnswer(f)
  const canSubmit = missing === null && !uploading

  async function onPickFiles(picked: FileList | null) {
    const list = Array.from(picked ?? []).slice(0, MAX_DEMAND_ATTACHMENTS - files.length)
    if (fileInput.current) fileInput.current.value = ''
    if (!list.length) return
    setUploading(true)
    try {
      for (const file of list) {
        try {
          const uploaded = await uploadDemandAttachment(file)
          setFiles((prev) => [...prev, uploaded])
        } catch (err) {
          toast.showToast({
            title: apiErrorMessage(err, `Couldn't attach ${file.name}`),
            intent: 'error',
          })
        }
      }
    } finally {
      setUploading(false)
    }
  }

  // Cancel used to throw away whatever had been typed, without a word.
  function onCancel() {
    const typed = Object.values(f).some((v) => typeof v === 'string' && v.trim()) || files.length
    if (typed && !window.confirm('Discard what you have written?')) return
    navigate('/')
  }

  function onSubmit() {
    if (!canSubmit || submit.isPending) return
    const dated = f.has_date === 'yes'
    submit.mutate(
      {
        title: f.title.trim(),
        description: f.description.trim(),
        related_work: f.related_work || undefined,
        related_work_details:
          f.related_work === 'yes' ? f.related_work_details.trim() || undefined : undefined,
        target_user: f.target_user.trim(),
        anticipated_benefit: f.anticipated_benefit.trim() || undefined,
        important_date: dated && f.important_date ? f.important_date : undefined,
        important_date_reason: dated ? f.important_date_reason.trim() || undefined : undefined,
        background: f.background.trim() || undefined,
        attachment_ids: files.map((a) => a.id),
        // Reused across a reload/retry so the server lands one record (#1264).
        idempotency_key: idempotencyKey,
      },
      {
        onSuccess: (d) => {
          // Retire the key so the next genuine submission from this tab mints a
          // fresh one instead of deduping against the demand just created.
          clearIdempotencyKey()
          // The page they land on says where it went; the toast must not
          // contradict it (#1368).
          toast.showToast({
            title:
              d.disposition === 'digital_council'
                ? 'Demand submitted — it was passed to DSAI.'
                : 'Demand submitted — we aim to confirm the next step within two working days.',
          })
          navigate(`/demand/${d.id}`)
        },
        onError: (err: unknown) => {
          toast.showToast({
            title: apiErrorMessage(err, 'Failed to submit demand'),
            intent: 'error',
          })
        },
      },
    )
  }

  return (
    <div className={styles.page}>
      <header className={styles.topbar}>
        <a
          className={styles.brand}
          href="/"
          onClick={(e) => {
            e.preventDefault()
            onCancel()
          }}
        >
          <img src="/dac-logo.svg" alt="" className={styles.brandLogo} />
          DACcelerate
        </a>
        <Button appearance="subtle" onClick={onCancel} data-testid="open-app-link">
          Open the app →
        </Button>
      </header>
      <div className={styles.root} data-testid="submit-demand-page">
        <div className={styles.header}>
          <DocumentAdd24Regular />
          <Title3>Submit a demand</Title3>
        </div>
        <Body1 className={styles.intro}>
          Describe what you need — the council routes it to the right team.
        </Body1>

        {me && (
          <div className={styles.submitter} data-testid="submit-submitter">
            <span aria-hidden="true">
              <UserAvatar name={me.name} size={36} />
            </span>
            <div className={styles.submitterText}>
              <Caption1 className={styles.hint}>Submitting as</Caption1>
              <span className={styles.submitterName}>{me.name}</span>
              <Caption1 className={styles.submitterEmail}>{me.email}</Caption1>
            </div>
          </div>
        )}

        <Field label="Short title" required>
          <Input
            value={f.title}
            onChange={(_, d) => set('title', d.value)}
            placeholder="e.g. Automated data cleaning for clinical trials"
            maxLength={255}
            autoFocus
            data-testid="submit-title"
          />
        </Field>

        <Field label="What problem, need or opportunity are you trying to address?" required>
          <Textarea
            value={f.description}
            onChange={(_, d) => set('description', d.value)}
            rows={4}
            placeholder="What are you trying to achieve, and what is difficult today?"
            data-testid="submit-description"
          />
        </Field>

        <Field
          label="Is this related to an existing Product Team, project or piece of work?"
          required
        >
          <RadioGroup
            layout="horizontal"
            value={f.related_work}
            onChange={(_, d) => set('related_work', d.value as RelatedWork)}
            data-testid="submit-related-work"
          >
            <Radio value="no" label="No" />
            <Radio value="yes" label="Yes" />
            <Radio value="not_sure" label="Not sure" />
          </RadioGroup>
        </Field>
        {f.related_work === 'yes' && (
          <Field label="Which one?">
            <Textarea
              value={f.related_work_details}
              onChange={(_, d) => set('related_work_details', d.value)}
              rows={2}
              placeholder="Team or project, a contact, a link"
              data-testid="submit-related-details"
            />
          </Field>
        )}

        <Field label="Who would benefit?" required>
          <Input
            value={f.target_user}
            onChange={(_, d) => set('target_user', d.value)}
            placeholder="Users, teams, functions or stakeholders"
            maxLength={255}
            data-testid="submit-target-user"
          />
        </Field>

        <div className={styles.optionalHeader}>More detail (optional)</div>

        <Field label="Expected benefit or impact">
          <Textarea
            value={f.anticipated_benefit}
            onChange={(_, d) => set('anticipated_benefit', d.value)}
            rows={2}
            placeholder="Scientific impact, time saved, better decisions, quality, less cost or risk. Estimates are fine."
            data-testid="submit-benefit"
          />
        </Field>

        <Field label="Is there an important date or dependency?">
          <RadioGroup
            layout="horizontal"
            value={f.has_date}
            onChange={(_, d) => set('has_date', d.value as 'no' | 'yes')}
            data-testid="submit-has-date"
          >
            <Radio value="no" label="No specific date" />
            <Radio value="yes" label="Yes" />
          </RadioGroup>
        </Field>
        {f.has_date === 'yes' && (
          <div className={styles.dateRow}>
            <Field label="Date">
              <Input
                type="date"
                value={f.important_date}
                onChange={(_, d) => set('important_date', d.value)}
                data-testid="submit-date"
              />
            </Field>
            <Field label="Why it matters" className={styles.dateReason}>
              <Input
                value={f.important_date_reason}
                onChange={(_, d) => set('important_date_reason', d.value)}
                placeholder="e.g. a filing, a steering committee"
                data-testid="submit-date-reason"
              />
            </Field>
          </div>
        )}

        <Field label="Anything else that would help us understand the request?">
          <Textarea
            value={f.background}
            onChange={(_, d) => set('background', d.value)}
            rows={3}
            placeholder="Context, links, earlier work"
            data-testid="submit-background"
          />
        </Field>

        <div className={styles.files}>
          <input
            ref={fileInput}
            type="file"
            multiple
            hidden
            accept={DEMAND_ATTACHMENT_ACCEPT}
            onChange={(e) => void onPickFiles(e.target.files)}
            data-testid="submit-files-input"
          />
          <Button
            appearance="secondary"
            icon={<Attach20Regular aria-hidden />}
            onClick={() => fileInput.current?.click()}
            disabled={uploading || submit.isPending || files.length >= MAX_DEMAND_ATTACHMENTS}
            data-testid="submit-attach"
          >
            {uploading ? 'Attaching…' : 'Attach files'}
          </Button>
          <Caption1 className={styles.hint}>
            Up to {MAX_DEMAND_ATTACHMENTS} files, 10 MB each
          </Caption1>
          {files.map((a) => (
            <div key={a.id} className={styles.fileRow} data-testid={`submit-file-${a.id}`}>
              <span className={styles.fileName}>{a.filename}</span>
              <Button
                appearance="subtle"
                size="small"
                icon={<Dismiss16Regular aria-hidden />}
                aria-label={`Remove ${a.filename}`}
                // The in-flight submission already carries this list.
                disabled={submit.isPending}
                onClick={() => setFiles((prev) => prev.filter((x) => x.id !== a.id))}
              />
            </div>
          ))}
        </div>

        <div className={styles.actions}>
          <Button
            appearance="primary"
            disabled={!canSubmit || submit.isPending}
            onClick={onSubmit}
            data-testid="submit-demand-btn"
          >
            {submit.isPending ? 'Submitting…' : 'Submit demand'}
          </Button>
          <Button
            appearance="subtle"
            onClick={onCancel}
            disabled={submit.isPending}
            data-testid="submit-cancel"
          >
            Cancel
          </Button>
        </div>
        {/* A disabled button that does not say what it is waiting for is a
            dead end for whoever cannot see why (#1374). */}
        {missing && (
          <Caption1 className={styles.hint} data-testid="submit-hint">
            {missing} — then you can submit.
          </Caption1>
        )}
      </div>
    </div>
  )
}
